'use client';

import { type CSSProperties, type MouseEvent, type ReactNode, useId, useLayoutEffect, useMemo, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import XLargeIcon from '../../../icons/react/icon-x-large';
import type { Space } from '../../../styles/tokens';
import { usePrefersReducedMotion } from '../../../util/accessibility';
import { buildResponsiveProps, type ResponsiveValue } from '../../../util/responsive';
import { sanitizeCSSVariables } from '../../../util/style';
import { VerticalStack } from '../../alignment/vertical-stack';
import { Button } from '../../button/button';
import { TypographyProviderReset } from '../../typography/context';
import { DialogHeader } from '../header/dialog-header';
import { findPinchableDialogs, MODAL_DATA_ATTRIBUTE_NAME, useMergedDialogOpenProps } from '../utils';
import dialogStyles from '../dialog.module.scss';
import styles from './modal.module.scss';

const dataAttrProps = { [MODAL_DATA_ATTRIBUTE_NAME]: true } as const;

const overlayTransition = {
  duration: 0.3,
  ease: [0.16, 1, 0.3, 1],
} as const;

const dialogContentTransition = {
  duration: 0.3,
  ease: 'easeOut',
} as const;

const overlayAnimationVariants = {
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  initial: { opacity: 0 },
} as const;

const dialogContentAnimationVariants = {
  animate: { left: '50%', bottom: '50%', y: '50%', x: '-50%' },
  exit: { left: '50%', bottom: 0, y: '100%', x: '-50%' },
  initial: { left: '50%', bottom: 0, y: '100%', x: '-50%' },
} as const;

// handleCloseMouseDown prevents the dialog from shifting positions when the close button is clicked,
// e.g., when a required input that had focus is blurred and renders an error message, shifting
// dialog content up and moving the close button
function handleCloseMouseDown(e: MouseEvent<HTMLButtonElement>) {
  e.preventDefault();
  e.stopPropagation();
}

export function findParentModal(element: HTMLElement | null): HTMLElement | null {
  if (!element) {
    return null;
  }

  if (element.matches(`[${MODAL_DATA_ATTRIBUTE_NAME}]`)) {
    return element;
  }

  return findParentModal(element.parentElement);
}

const componentStyleName = 'modal';

const defaultCloseIcon = <XLargeIcon aria-hidden />;

export type ModalVariant = 'normal' | 'simple';

export interface ModalBaseProps {
  children?: ReactNode;
  className?: string;
  contentContainerClassName?: string;
  contentPadding?: 'compact' | 'normal';
  description: { rendered: ReactNode } | string;
  dialogClassName?: string;
  gap?: ResponsiveValue<Space>;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  style?: CSSProperties;
  trigger: ReactNode;
  triggerIsPrimitive?: boolean;
  width?: ResponsiveValue<number | string>;
  withScrollingContent?: boolean;
  withoutOverlay?: boolean;
}

export type ModalNormalProps = ModalBaseProps & {
  variant?: 'normal';
  closeButtonIcon?: ReactNode;
  headerClassName?: string;
  headerRightAction?: ReactNode;
  title: ReactNode;
};

export type ModalSimpleProps = ModalBaseProps & {
  variant: 'simple';
  title: string;
};

export const ModalActivator = DialogPrimitive.Trigger;

export type ModalProps = (ModalSimpleProps & { variant: 'simple' }) | (ModalNormalProps & { variant?: 'normal' });

function ModalHeader({
  closeButtonIcon = defaultCloseIcon,
  headerClassName,
  headerRightAction,
  title,
}: Pick<ModalNormalProps, 'closeButtonIcon' | 'headerClassName' | 'headerRightAction' | 'title'>) {
  return (
    <DialogHeader
      className={headerClassName}
      leftAction={
        closeButtonIcon ? (
          <DialogPrimitive.Close aria-label="Close dialog" asChild className={styles.close} onMouseDown={handleCloseMouseDown}>
            <Button affixIcon={closeButtonIcon} size="small" variant="secondary" />
          </DialogPrimitive.Close>
        ) : null
      }
      rightAction={headerRightAction}
      title={<DialogPrimitive.Title>{title}</DialogPrimitive.Title>}
    />
  );
}

export function Modal({
  children,
  className,
  contentContainerClassName,
  contentPadding = 'normal',
  description,
  dialogClassName,
  gap = 4,
  isOpen: providedIsOpen,
  onOpenChange: providedOnOpenChange,
  style,
  title,
  trigger,
  triggerIsPrimitive,
  width,
  withScrollingContent,
  variant = 'normal',
  withoutOverlay,
  ...rest
}: ModalProps) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });
  const baseStyle = useMemo(
    () =>
      sanitizeCSSVariables({
        ...style,
        ...buildResponsiveProps(componentStyleName, 'width', width),
      }),
    [style, width],
  );

  // Pinch the root content when a modal is open, revert when all modals are closed
  useLayoutEffect(() => {
    if (isOpen) {
      document.body.classList.add(dialogStyles.pinchedContent);
    }

    return () => {
      const pinchableDialogs = Array.from(findPinchableDialogs()).filter((d) => d !== ref.current);

      if (!pinchableDialogs.length) {
        document.body.classList.remove(dialogStyles.pinchedContent);
      }
    };
  }, [isOpen]);

  const baseOverlayClassName = clsx(styles.overlay, className);
  const baseContentClassName = clsx(
    dialogStyles.dialog,
    styles.modal,
    styles[`variant-${variant}`],
    styles[`content-padding-${contentPadding}`],
    dialogClassName,
  );

  const baseModalContent = (
    <VerticalStack className={clsx(withScrollingContent && styles.withScrollingContent)} gap={gap}>
      {variant === 'simple' ? (
        <DialogPrimitive.Title className={styles.visuallyHidden}>{title}</DialogPrimitive.Title>
      ) : (
        <ModalHeader title={title} {...rest} />
      )}
      <div className={clsx(styles.contentContainer, contentContainerClassName)}>
        <DialogPrimitive.Description className={clsx(styles.description, typeof description === 'string' && styles.visuallyHidden)}>
          {typeof description === 'string' ? description : description.rendered}
        </DialogPrimitive.Description>
        {children}
      </div>
    </VerticalStack>
  );

  return (
    <TypographyProviderReset>
      <DialogPrimitive.Root onOpenChange={onOpenChange} open={isOpen}>
        {triggerIsPrimitive ? trigger : <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>}

        {prefersReducedMotion ? (
          <DialogPrimitive.Portal>
            {withoutOverlay ? null : <DialogPrimitive.Overlay className={baseOverlayClassName} />}

            <DialogPrimitive.Content
              {...dataAttrProps}
              className={clsx(baseContentClassName, styles.withoutAnimation)}
              style={baseStyle}
              {...rest}
              ref={ref}
            >
              {baseModalContent}
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        ) : (
          <AnimatePresence>
            {isOpen ? (
              <DialogPrimitive.Portal forceMount key={`${id}-portal`}>
                {withoutOverlay ? null : (
                  <DialogPrimitive.Overlay asChild>
                    <motion.div {...overlayAnimationVariants} className={baseOverlayClassName} transition={overlayTransition} />
                  </DialogPrimitive.Overlay>
                )}

                <DialogPrimitive.Content asChild>
                  <motion.div
                    {...dataAttrProps}
                    {...dialogContentAnimationVariants}
                    className={baseContentClassName}
                    style={baseStyle}
                    transition={dialogContentTransition}
                    {...rest}
                    ref={ref}
                  >
                    {baseModalContent}
                  </motion.div>
                </DialogPrimitive.Content>
              </DialogPrimitive.Portal>
            ) : null}
          </AnimatePresence>
        )}
      </DialogPrimitive.Root>
    </TypographyProviderReset>
  );
}
